import React from 'react';
import { Checkbox } from 'primereact/checkbox';
import clsx from 'clsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { PrefillOptions } from '@components/RingilComponents/expeditionPrefillOptions.ts';

interface ComponentProps {
  value: PrefillOptions;
  onChange: (patch: Partial<PrefillOptions>) => void;
  editDistributionAllowed: boolean;
  isSourceLinkedToSn?: boolean;
}

const ExpeditionPrefillCheckboxes: React.FC<ComponentProps> = ({ value, onChange, editDistributionAllowed, isSourceLinkedToSn }) => {
  const { tr } = useTranslationLgs();

  const checkboxRow = (opts: { id: string; checked: boolean; label: string; subtitle?: string; disabled?: boolean; strong?: boolean; onChange?: (checked: boolean) => void }) => (
    <div className="flex items-center" key={opts.id}>
      <Checkbox
        inputId={opts.id}
        checked={opts.checked}
        disabled={opts.disabled}
        onChange={e => opts.onChange?.(e.checked)}
        className="mr-3"
      />
      <label htmlFor={opts.id} className="cursor-pointer">
        <div className={clsx('text-sm text-secondary', opts.strong ? 'font-medium' : 'font-normal')}>{opts.label}</div>
        {opts.subtitle && <div className="text-xs font-normal text-tertiary">{opts.subtitle}</div>}
      </label>
    </div>
  );

  const routeOn = value.routeAndParameters;

  // route is the master gate — flipping it flips every other field, both directions
  const flipAll = (c: boolean): Partial<PrefillOptions> => ({
    routeAndParameters: c,
    cargoAndOrders: c,
    labels: c,
    internalInfo: c,
    documents: c,
    carrierSelection: c,
    carrierNote: c,
  });

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-content-quaternary p-4">
      {checkboxRow({
        id: 'routeAndParameters',
        checked: routeOn,
        strong: true,
        label: tr('PrefillFromFavoritesSidebar.routeAndParameters', 'Route and transport parameters'),
        subtitle: tr('PrefillFromFavoritesSidebar.routeAndParametersSubtitle', 'E.g. carrier requirements, cargo type...'),
        onChange: c => onChange(flipAll(c)),
      })}
      <div className="flex flex-col gap-3 pl-7">
        {checkboxRow({
          id: 'cargoAndOrders',
          checked: value.cargoAndOrders,
          disabled: !routeOn,
          label: tr('PrefillFromFavoritesSidebar.cargoAndOrders', 'Cargo and orders'),
          onChange: c => onChange({ cargoAndOrders: c }),
        })}
        {checkboxRow({
          id: 'labels',
          checked: value.labels,
          disabled: !routeOn,
          label: tr('PrefillFromFavoritesSidebar.labels', 'Labels'),
          onChange: c => onChange({ labels: c }),
        })}
        {checkboxRow({
          id: 'internalInfo',
          checked: value.internalInfo,
          disabled: !routeOn,
          label: tr('PrefillFromFavoritesSidebar.internalInfo', 'Internal info'),
          onChange: c => onChange({ internalInfo: c }),
        })}
        {checkboxRow({
          id: 'documents',
          checked: value.documents,
          disabled: !routeOn,
          label: tr('PrefillFromFavoritesSidebar.documents', 'Documents'),
          onChange: c => onChange({ documents: c }),
        })}
        {editDistributionAllowed && checkboxRow({
          id: 'carrierSelection',
          checked: value.carrierSelection,
          disabled: !routeOn,
          label: tr('PrefillFromFavoritesSidebar.carrierSelection', 'Carrier selection'),
          onChange: c => onChange({ carrierSelection: c }),
        })}
        {checkboxRow({
          id: 'carrierNote',
          checked: value.carrierNote,
          disabled: !routeOn,
          label: tr('CarrierNoteTag.title', 'Note for carrier'),
          onChange: c => onChange({ carrierNote: c }),
        })}
      </div>
      {isSourceLinkedToSn && (
        <div className="flex flex-col gap-3 border-t border-content-quaternary pt-3">
          {checkboxRow({
            id: 'linkToSN',
            checked: !!value.linkToSN,
            label: tr('DuplicateExpeditionDialog.linkovatNaSn', 'Linkovat na SN'),
            onChange: c => onChange({ linkToSN: c }),
          })}
          {checkboxRow({
            id: 'copySNAttachments',
            checked: !!value.copySNAttachments,
            label: tr('DuplicateExpeditionDialog.kopirovatPrilohyZSn', 'Kopirovat prilohy z SN'),
            onChange: c => onChange(c ? { copySNAttachments: true } : { copySNAttachments: false, markAsPrivate: false }),
          })}
          {checkboxRow({
            id: 'markAsPrivate',
            checked: !!value.markAsPrivate,
            disabled: !value.copySNAttachments,
            label: tr('DuplicateExpeditionDialog.markAsPrivate', 'Mark copied attachments as private'),
            onChange: c => onChange({ markAsPrivate: c }),
          })}
        </div>
      )}
    </div>
  );
};

export default ExpeditionPrefillCheckboxes;
